import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

export interface AdBatchItem {
  adKey: string;
  viewCount: number;
  properties: Record<string, any>;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface AdBatchState {
  adBatch: Record<string, AdBatchItem>;
  lastSentTimestamp: string | null;
  batchStartTimestamp: string | null;
}

const initialState: AdBatchState = {
  adBatch: {},
  lastSentTimestamp: null,
  batchStartTimestamp: null,
};

export const generateAdKey = (properties: Record<string, any>): string => {
  const {
    banner_redirection_link,
    banner_action_enum,
    advertisement_type_enum,
    product_type_enum,
    banner_media_type,
  } = properties;

  return `${
    banner_redirection_link || "no-link"
  }_${banner_action_enum}_${advertisement_type_enum}_${product_type_enum}_${banner_media_type}`;
};

const adBatchSlice = createSlice({
  name: "adBatch",
  initialState,
  reducers: {
    addAdToBatch: (state, action: PayloadAction<Record<string, any>>) => {
      const properties = action.payload;
      const adKey = generateAdKey(properties);
      const now = new Date().toISOString();

      if (!state.batchStartTimestamp) {
        state.batchStartTimestamp = now;
      }

      if (state.adBatch[adKey]) {
        state.adBatch[adKey].viewCount += 1;
        state.adBatch[adKey].lastSeenAt = now;
      } else {
        state.adBatch[adKey] = {
          adKey,
          viewCount: 1,
          properties: {
            ...properties,
            timestamp: undefined,
          },
          firstSeenAt: now,
          lastSeenAt: now,
        };
      }
    },

    clearBatch: (state) => {
      state.adBatch = {};
      state.lastSentTimestamp = new Date().toISOString();
      state.batchStartTimestamp = null;
    },

    updateLastSentTimestamp: (state, action: PayloadAction<string>) => {
      state.lastSentTimestamp = action.payload;
    },

    resetAdBatchState: () => initialState,
  },
});

export const {
  addAdToBatch,
  clearBatch,
  updateLastSentTimestamp,
  resetAdBatchState,
} = adBatchSlice.actions;

export default adBatchSlice.reducer;

export const useAdBatchSlice = () => {
  const adBatchState = useSelector(
    (state: any) => state.adBatch as AdBatchState
  );

  const getAdBatch = () => adBatchState?.adBatch ?? {};

  const getLastSentTimestamp = () => adBatchState?.lastSentTimestamp;

  const getBatchStartTimestamp = () => adBatchState?.batchStartTimestamp;

  const hasUnsentAds = () =>
    Object.keys(adBatchState?.adBatch ?? {}).length > 0;

  const getAdCount = () => Object.keys(adBatchState?.adBatch ?? {}).length;

  const getTotalViewCount = () =>
    Object.values(adBatchState?.adBatch ?? {}).reduce(
      (total, ad) => total + ad.viewCount,
      0
    );

  const isBatchReadyToSend = (interval: number) => {
    if (!hasUnsentAds()) return false;

    const batchStart = adBatchState?.batchStartTimestamp;
    if (!batchStart) return false;

    const batchStartTime = new Date(batchStart).getTime();
    const now = Date.now();

    return now - batchStartTime >= interval;
  };

  const hasStaleAds = (interval: number) => {
    const batchStart = adBatchState?.batchStartTimestamp;
    if (!batchStart || !hasUnsentAds()) return false;

    const batchStartTime = new Date(batchStart).getTime();
    const now = Date.now();

    return now - batchStartTime >= interval;
  };

  return {
    adBatch: adBatchState?.adBatch ?? {},
    lastSentTimestamp: adBatchState?.lastSentTimestamp,
    batchStartTimestamp: adBatchState?.batchStartTimestamp,
    getAdBatch,
    getLastSentTimestamp,
    getBatchStartTimestamp,
    hasUnsentAds,
    getAdCount,
    getTotalViewCount,
    isBatchReadyToSend,
    hasStaleAds,
  };
};
